import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/Modal";
import { clampMinutes, formatMinutes, isValidTimeExpression, parseTimeExpression } from "@/utils/timeTracking";
import { Task } from "@/types";

type AdjustmentStrategy = "auto" | "set" | "none";

interface LogWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    time_spent_minutes: number;
    started_at?: string;
    comment?: string;
    adjust_remaining: AdjustmentStrategy;
    remaining_time_minutes?: number | null;
  }) => Promise<void>;
  task: Task;
  isSubmitting?: boolean;
}

const formatDateTimeLocal = (iso?: string) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value: number) => value.toString().padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const LogWorkModal: React.FC<LogWorkModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  task,
  isSubmitting = false,
}) => {
  const [timeInput, setTimeInput] = useState("");
  const [remainingStrategy, setRemainingStrategy] = useState<AdjustmentStrategy>("auto");
  const [remainingInput, setRemainingInput] = useState("");
  const [comment, setComment] = useState("");
  const [startedAt, setStartedAt] = useState<string>(() => formatDateTimeLocal(new Date().toISOString()));
  const [error, setError] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeInput("");
      setRemainingStrategy("auto");
      setRemainingInput("");
      setComment("");
      setStartedAt(formatDateTimeLocal(new Date().toISOString()));
      setError("");
    }
  }, [isOpen]);

  const remainingPlaceholder = useMemo(() => {
    if (typeof task.remaining_time_minutes === "number") {
      return formatMinutes(task.remaining_time_minutes);
    }
    if (typeof task.original_estimate_minutes === "number") {
      return formatMinutes(task.original_estimate_minutes);
    }
    return "e.g. 2h";
  }, [task.remaining_time_minutes, task.original_estimate_minutes]);

  const validate = (): { timeSpent: number; remaining?: number | null } | null => {
    const normalized = timeInput.trim().toLowerCase();
    if (!normalized) {
      setError("Please enter time spent (e.g. 1h 30m)");
      return null;
    }
    if (!isValidTimeExpression(normalized)) {
      setError("Invalid time format. Use combinations of w/d/h/m (e.g. 1w 2d 3h 30m)");
      return null;
    }
    const minutes = clampMinutes(parseTimeExpression(normalized));
    if (minutes <= 0) {
      setError("Time spent must be greater than zero");
      return null;
    }

    if (remainingStrategy === "set") {
      const remNormalized = remainingInput.trim().toLowerCase();
      if (!remNormalized) {
        setError("Provide the new remaining estimate (e.g. 3h)");
        return null;
      }
      if (!isValidTimeExpression(remNormalized)) {
        setError("Invalid remaining time format. Use w/d/h/m");
        return null;
      }
      const remaining = clampMinutes(parseTimeExpression(remNormalized));
      return { timeSpent: minutes, remaining };
    }

    return { timeSpent: minutes, remaining: null };
  };

  const handleSubmit = async () => {
    const result = validate();
    if (!result) return;
    setError("");
    setIsSaving(true);
    try {
      // Convert datetime-local to ISO UTC string
      // datetime-local gives time in user's local timezone, we need to convert to UTC
      let startedAtISO: string | undefined = undefined;
      if (startedAt) {
        // Create date from datetime-local string (treats as local time)
        const localDate = new Date(startedAt);
        // Convert to UTC ISO string
        startedAtISO = localDate.toISOString();
      }
      
      await onSubmit({
        time_spent_minutes: result.timeSpent,
        started_at: startedAtISO,
        comment: comment.trim() || undefined,
        adjust_remaining: remainingStrategy,
        remaining_time_minutes: remainingStrategy === "set" ? result.remaining ?? 0 : undefined,
      });
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message;
      setError(detail || "Failed to log work");
    } finally {
      setIsSaving(false);
    }
  };

  const disableSubmit = isSaving || isSubmitting;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!disableSubmit) onClose();
      }}
      title="Log Work"
      size="sm"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time spent</label>
          <input
            type="text"
            spellCheck={false}
            placeholder="e.g. 1h 30m"
            value={timeInput}
            onChange={(e) => setTimeInput(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="datetime-local"
            value={startedAt}
            max={formatDateTimeLocal(new Date().toISOString())}
            onChange={(e) => setStartedAt(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-gray-700">Remaining estimate</legend>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="remainingStrategy"
              value="auto"
              checked={remainingStrategy === "auto"}
              onChange={() => setRemainingStrategy("auto")}
              className="mt-1"
            />
            <span>
              <span className="font-medium text-gray-900 block">Reduce automatically</span>
              <span className="text-xs text-gray-500">
                Subtract logged time from the current remaining estimate.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="remainingStrategy"
              value="set"
              checked={remainingStrategy === "set"}
              onChange={() => setRemainingStrategy("set")}
              className="mt-1"
            />
            <span className="w-full">
              <span className="font-medium text-gray-900 block">Set to a specific value</span>
              <div className="mt-2">
                <input
                  type="text"
                  spellCheck={false}
                  placeholder={remainingPlaceholder}
                  value={remainingInput}
                  onChange={(e) => setRemainingInput(e.target.value)}
                  disabled={remainingStrategy !== "set"}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="remainingStrategy"
              value="none"
              checked={remainingStrategy === "none"}
              onChange={() => setRemainingStrategy("none")}
              className="mt-1"
            />
            <span>
              <span className="font-medium text-gray-900 block">Do not change</span>
              <span className="text-xs text-gray-500">
                Keep the remaining estimate unchanged.
              </span>
            </span>
          </label>
        </fieldset>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Comment (optional)</label>
          <textarea
            rows={3}
            placeholder="Add context for this worklog"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {error && <div className="text-sm text-red-500">{error}</div>}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={disableSubmit}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded border border-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disableSubmit}
            className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
          >
            {disableSubmit ? "Saving..." : "Log work"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

